import React, { useCallback, useState } from "react";
import { useDropzone } from 'react-dropzone';
import classnames from "classnames";
import { getStorage, ref, uploadBytesResumable } from "firebase/storage";
import app from "fb";
import { getUid } from "backend/auth";
import getFilePath from "utils/getFilePath";
import { toast } from "react-toastify";

interface PropsType {
    onChange?: (value: any) => void;
    customMetadata?: { [key: string]: string };
    size?: "medium" | "large";
    disabled?: boolean;
}
export function InputImageUpdate({
    onChange,
    customMetadata,
    size = "medium",
    disabled = false,
}: PropsType) {
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [err, setErr] = useState<string>('');
    const toastId = React.useRef(null);

    const onDrop = useCallback((acceptedFiles: any) => {
        if (!disabled && acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = function () {
                const width = img.naturalWidth;
                const height = img.naturalHeight;

                const size = file.size;

                // Convert size to MB
                const sizeMB = size / 1000000;
                if (sizeMB > 2) {
                    toast.error("Invalid file size. Size should be maximum 2MB.");
                    return;
                }

                const megapixels = width * height / 1000000;
                if (megapixels > 25) {
                    toast.error("Invalid image dimensions. The image size should be maximum 20 megapixels.");
                    return;
                }

                setErr('');
                setUrl(url);

                const storage = getStorage(app);
                const uid = getUid();

                if (!uid) {
                    setErr('Invalid user.');
                    return;
                }

                const path = getFilePath(file.name);
                const fullPath = `images/${uid}/${path}`;

                const storageRef = ref(storage, fullPath);
                const uploadTask = uploadBytesResumable(storageRef, file, {
                    contentType: file.type,
                    customMetadata: customMetadata,
                });

                uploadTask.on('state_changed',
                    (snapshot: { bytesTransferred: number; totalBytes: number; }) => {
                        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes);
                        // check if we already displayed a toast
                        if (toastId.current == null) {
                            // @ts-ignore
                            toastId.current = toast('Upload in Progress', { progress: 0.01 });
                        } else {
                            toast.update(toastId.current, { progress });
                        }
                    },
                    (error: any) => {
                        setUrl(undefined);
                        // Handle unsuccessful uploads
                        console.log('Upload failed', error);
                    },
                    () => {
                        // Handle successful uploads on complete
                        console.log('Upload completed');
                        setUrl(undefined);
                        // @ts-ignore
                        toast.done(toastId.current);
                        onChange && onChange(fullPath);
                    }
                );
            };
            img.src = url;
        }
    }, [onChange, customMetadata, disabled]);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            'image/png': [],
            'image/jpeg': [],
            'image/gif': [],
        },
        multiple: false,
        disabled: disabled || !!url,
    });

    if (size === "large") {
        return (
            <div className="relative flex items-center justify-center w-full" {...getRootProps()}>
                <label
                    htmlFor="dropzone-file"
                    className={
                        classnames(
                            "flex flex-col items-center justify-center w-full border-2 border-gray-300 border-dashed rounded-lg overflow-hidden dark:border-gray-60",
                            {
                                "cursor-pointer dark:hover:border-gray-500 dark:hover:bg-gray-600 dark:hover:bg-bray-800 hover:bg-gray-100": !disabled && !url,
                                "bg-gray-50": !disabled,
                                "bg-gray-200 cursor-not-allowed": disabled,
                            }
                        )
                    }
                >
                    <div className={
                        classnames(
                            "flex flex-col items-center justify-center h-64",
                            {
                                "pt-5 pb-6": !url
                            },
                        )
                    }>
                        {!!url ?
                            (
                                <div className="relative w-full h-full">
                                    <img src={url} alt="Preview" className="object-contain h-full w-full opacity-40" />
                                    <div className="flex justify-center items-center absolute top-0 left-0 right-0 bottom-0">
                                        <svg aria-hidden="true" role="status" className="inline w-8 h-8 text-black animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                                        </svg>
                                    </div>
                                </div>
                            )
                            : <>
                                <svg
                                    className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                    aria-hidden="true"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 20 16"
                                >
                                    <path
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                    />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Click to upload</span> or drag and
                                    drop
                                </p>
                                <p className={classnames('text-center text-xs text-gray-500 dark:text-gray-400 px-4 md:px-10', { 'text-red-500': !!err })}>
                                    {err !== '' ? err : "(The image size should be maximum 2MB and 20 megapixels)"}
                                </p>
                            </>}
                    </div>
                    <input {...getInputProps()} />
                </label>
            </div>
        );
    }

    return (
        <div className="relative flex items-center justify-center w-full max-w-56" {...getRootProps()}>
            <label
                htmlFor="dropzone-file"
                className={
                    classnames(
                        "flex flex-col items-center justify-center w-full aspect-square rounded-lg overflow-hidden dark:bg-gray-700 dark:border-gray-600",
                        {
                            "cursor-pointer dark:hover:border-gray-500 dark:hover:bg-gray-600 dark:hover:bg-bray-800 hover:bg-gray-200": !disabled && !url,
                            "bg-gray-50": !disabled,
                            "bg-gray-200 cursor-not-allowed": disabled,
                        }
                    )
                }
            >
                {
                    !!url ? (
                        <div className="relative w-full h-full">
                            <img src={url} alt="Preview" className="object-contain h-full w-full opacity-40" />
                            <div className="flex justify-center items-center absolute top-0 left-0 right-0 bottom-0">
                                <svg aria-hidden="true" role="status" className="inline w-4 h-4 text-black animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                                </svg>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <svg
                                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                                aria-hidden="true"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 18 18"
                            >
                                <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9 1v16M1 9h16"
                                />
                            </svg>
                        </div>
                    )
                }

                <input {...getInputProps()} />
            </label>
        </div>
    );

}
